import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  PlayArrow as RetryIcon,
  Delete as DeleteIcon,
  Autorenew as RegenerateIcon,
  Person as ProfileIcon,
  RadioButtonUnchecked as InactiveIcon,
  CheckCircle as ActiveIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, getDocs, orderBy, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { getUserForecasts } from '../utils/forecastService';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import CareerReportCard from './CareerReportCard';
import { getProfileIdForUser } from '../utils/profileService';
import { bulkDeleteReports, checkReportLimit } from '../utils/careerReportService';

function UserDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = getAuth();
  const user = auth.currentUser;
  const { settings: platformSettings } = usePlatformSettings();
  
  // Check URL params for tab parameter (e.g., /dashboard?tab=cognitive)
  // Default to career tab (0) since that's the primary product
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'cognitive' ? 1 : 0;
  
  const [forecasts, setForecasts] = useState([]);
  const [careerReports, setCareerReports] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab); // 0 = career, 1 = cognitive
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryingForecast, setRetryingForecast] = useState(null);
  const [regeneratingForecast, setRegeneratingForecast] = useState(null);
  const [deletingForecast, setDeletingForecast] = useState(null);
  const [settingActiveForecast, setSettingActiveForecast] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  
  // Bulk selection and delete
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [reportLimit, setReportLimit] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    loadForecasts(true); // Show spinner on initial load
    loadCareerReports(true);
    
    // Auto-refresh every 5 seconds (without spinner)
    const interval = setInterval(() => {
      loadForecasts(false); // Silent refresh in background
      loadCareerReports(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [user]);

  // Stop auto-refresh if all forecasts are complete or failed
  useEffect(() => {
    const hasActiveForecasts = forecasts.some(f => 
      f.status === 'processing' || 
      f.status === 'payment_complete' || 
      (f.status === 'analyzed' && !f.pdfUrl)
    );
    
    // If no active forecasts, we can slow down or stop polling
    // But we keep it running in case user triggers new analysis
  }, [forecasts]);

  const loadForecasts = async (showLoadingSpinner = false) => {
    try {
      // Only show loading spinner on initial load, not on auto-refresh
      if (showLoadingSpinner) {
        setLoading(true);
      }
      const userForecasts = await getUserForecasts(user.uid);
      setForecasts(userForecasts);
      setLastRefresh(Date.now());
      setError(null);
      console.log('🔄 Dashboard refreshed:', new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Error loading forecasts:', err);
      setError('Failed to load forecasts. Please try again.');
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  const loadCareerReports = async (showLoadingSpinner = false) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }
      const db = getFirestore();
      const reportsRef = collection(db, 'careerReports');
      const q = query(reportsRef, where('userId', '==', user.uid), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setCareerReports(reports);
      console.log('🔄 Career reports refreshed:', reports.length);
    } catch (err) {
      console.error('Error loading career reports:', err);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'analyzed':
        return 'success';
      case 'processing':
      case 'payment_complete':
        return 'warning';
      case 'failed':
        return 'error';
      case 'ready_for_payment':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusText = (forecast) => {
    const status = forecast.status;
    
    switch (status) {
      case 'completed':
        return forecast.pdfUrl ? 'Ready' : 'Generating PDF...';
      case 'analyzed':
        return forecast.pdfUrl ? 'Ready' : 'Finalizing Report...';
      case 'processing':
        return 'Building Your Profile...';
      case 'payment_complete':
        return 'Getting Started...';
      case 'failed':
        return 'Failed';
      case 'ready_for_payment':
        return 'Pending Payment';
      default:
        return status;
    }
  };

  const handleViewReport = (forecast) => {
    if (forecast.pdfUrl) {
      window.open(forecast.pdfUrl, '_blank');
    } else if (forecast.aiAnalysis) {
      // Show inline report view (future enhancement)
      alert('PDF generation in progress. Please check back soon.');
    }
  };

  const handleDownloadReport = (forecast) => {
    if (forecast.pdfUrl) {
      const link = document.createElement('a');
      link.href = forecast.pdfUrl;
      link.download = `${platformSettings.platformName || 'Mindtrace'}-Report-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    }
  };

  const handleRetryAnalysis = async (forecast) => {
    try {
      setRetryingForecast(forecast.id);
      console.log('🔄 Manually triggering analysis for forecast:', forecast.id);
      
      const functions = getFunctions();
      const analyzePatternLock = httpsCallable(functions, 'analyzePatternLock');
      
      const result = await analyzePatternLock({ forecastId: forecast.id });
      
      console.log('✅ Analysis triggered successfully:', result.data);
      alert('✅ Analysis started! Your report will be ready in 30-60 seconds.');
      
      // Reload forecasts after a short delay
      setTimeout(() => {
        loadForecasts();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Analysis call failed:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error details:', error.details);
      
      alert(`❌ Analysis failed: ${error.message}\n\nError code: ${error.code}\n\nPlease check:\n1. Admin → AI Agents → Both agents enabled\n2. Admin → AI Agents → API keys configured\n3. Browser console for detailed logs`);
    } finally {
      setRetryingForecast(null);
    }
  };

  const handleRegenerateReport = async (forecast) => {
    if (!window.confirm('Regenerate this report? This will create a new analysis using the same data. You can only do this once per report.')) {
      return;
    }

    try {
      setRegeneratingForecast(forecast.id);
      console.log('🔄 Regenerating report for forecast:', forecast.id);
      
      // Mark as regenerated in Firestore
      const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
      const db = getFirestore();
      await updateDoc(doc(db, 'forecasts', forecast.id), {
        regenerated: true,
        regeneratedAt: new Date().toISOString()
      });
      
      const functions = getFunctions();
      const analyzePatternLock = httpsCallable(functions, 'analyzePatternLock');
      
      const result = await analyzePatternLock({ forecastId: forecast.id });
      
      console.log('✅ Regeneration triggered successfully:', result.data);
      alert('✅ Regenerating report! Your new report will be ready in 30-60 seconds.');
      
      // Reload forecasts after a short delay
      setTimeout(() => {
        loadForecasts();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Regeneration failed:', error);
      alert(`❌ Regeneration failed: ${error.message}`);
    } finally {
      setRegeneratingForecast(null);
    }
  };

  const handleDeleteReport = async (forecast) => {
    if (!window.confirm('Delete this report? This action cannot be undone. All your data for this forecast will be permanently removed.')) {
      return;
    }

    try {
      setDeletingForecast(forecast.id);
      console.log('🗑️ Deleting forecast:', forecast.id);
      
      // Import Firestore
      const { getFirestore, doc, deleteDoc } = await import('firebase/firestore');
      const db = getFirestore();
      
      // Delete the forecast document
      await deleteDoc(doc(db, 'forecasts', forecast.id));
      
      console.log('✅ Forecast deleted successfully');
      alert('✅ Report deleted successfully.');
      
      // Reload forecasts
      loadForecasts();
      
    } catch (error) {
      console.error('❌ Delete failed:', error);
      alert(`❌ Delete failed: ${error.message}`);
    } finally {
      setDeletingForecast(null);
    }
  };

  // Set a cognitive profile as active
  const handleSetActiveForecast = async (forecast) => {
    try {
      setSettingActiveForecast(forecast.id);
      console.log('⭐ Setting active forecast:', forecast.id);
      
      const db = getFirestore();
      const batch = writeBatch(db);
      
      // First, deactivate all other forecasts for this user
      const otherActiveForecasts = forecasts.filter(f => f.id !== forecast.id && f.isActive);
      for (const otherForecast of otherActiveForecasts) {
        const otherRef = doc(db, 'forecasts', otherForecast.id);
        batch.update(otherRef, { isActive: false });
      }
      
      // Set this forecast as active
      const forecastRef = doc(db, 'forecasts', forecast.id);
      batch.update(forecastRef, { isActive: true });
      
      await batch.commit();
      
      console.log('✅ Active forecast set successfully');
      
      // Update local state
      setForecasts(prev => prev.map(f => ({
        ...f,
        isActive: f.id === forecast.id
      })));
      
    } catch (error) {
      console.error('❌ Set active failed:', error);
      alert(`❌ Failed to set active: ${error.message}`);
    } finally {
      setSettingActiveForecast(null);
    }
  };

  // Toggle report selection for bulk delete
  const handleReportSelect = (reportId, isSelected) => {
    setSelectedReports(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(reportId);
      } else {
        newSet.delete(reportId);
      }
      return newSet;
    });
  };

  // Bulk delete selected reports
  const handleBulkDelete = async () => {
    if (selectedReports.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedReports.size} selected report(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setBulkDeleting(true);
      const result = await bulkDeleteReports(Array.from(selectedReports));
      
      alert(`✅ Deleted ${result.deleted} report(s)${result.failed > 0 ? `. ${result.failed} failed.` : ''}`);
      
      setSelectedReports(new Set());
      setBulkDeleteMode(false);
      loadCareerReports();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete some reports');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Check report limits
  useEffect(() => {
    if (user) {
      checkReportLimit(user.uid).then(setReportLimit);
    }
  }, [user, careerReports]);

  if (loading && forecasts.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress sx={{ color: '#00FFF5' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/')} sx={{ color: '#737373' }}>
                <BackIcon />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'monospace',
                  color: '#fafafa',
                  fontWeight: 600
                }}
              >
                Career Builder
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton onClick={() => {loadForecasts(); loadCareerReports();}} sx={{ color: '#737373' }}>
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#fafafa',
                  color: '#0a0a0a',
                  '&:hover': {
                    backgroundColor: '#e5e5e5'
                  }
                }}
              >
                New Analysis
              </Button>
            </Box>
          </Box>
          
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: '#262626',
              '& .MuiTab-root': {
                color: '#737373',
                fontFamily: 'monospace',
                textTransform: 'none',
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: '#00FFF5'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#00FFF5'
              }
            }}
          >
            <Tab label={`Career Analysis (${careerReports.length})`} />
            <Tab label={`Cognitive Profiles (${forecasts.length})`} />
          </Tabs>
          
          <Typography
            variant="caption"
            sx={{
              color: '#737373',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              display: 'block',
              mt: 1
            }}
          >
            Auto-refreshing every 5 seconds • Last updated: {new Date(lastRefresh).toLocaleTimeString()}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tab Content */}
        {activeTab === 0 ? (
          // Career Analysis Tab (now first/default)
          careerReports.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: '#171717',
                border: '1px solid #262626',
                borderRadius: '12px'
              }}
            >
              <Typography variant="h6" sx={{ color: '#fafafa', mb: 2, fontFamily: 'monospace' }}>
                No career analyses yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373', mb: 3 }}>
                Level up your career with data-driven gap analysis and personalized training plans.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#00FFF5',
                  color: '#0a0a0a',
                  '&:hover': {
                    backgroundColor: '#00CCC5'
                  }
                }}
              >
                Start Career Analysis
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Retention Policy & Limits Info */}
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#0d0d0d',
                  border: '1px solid #262626',
                  borderRadius: '8px'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#a1a1aa', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      📋 <strong>Retention:</strong> Active plans stay indefinitely. Draft plans are auto-deleted after 30 days of inactivity. Max 5 saved plans.
                    </Typography>
                    {reportLimit && (
                      <Typography variant="caption" sx={{ color: '#737373', fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                        {reportLimit.message}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {bulkDeleteMode ? (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleBulkDelete}
                          disabled={selectedReports.size === 0 || bulkDeleting}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            backgroundColor: '#ef4444',
                            '&:hover': { backgroundColor: '#dc2626' }
                          }}
                        >
                          {bulkDeleting ? 'Deleting...' : `Delete (${selectedReports.size})`}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setBulkDeleteMode(false);
                            setSelectedReports(new Set());
                          }}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#737373',
                            borderColor: '#404040'
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setBulkDeleteMode(true)}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          color: '#737373',
                          borderColor: '#404040',
                          '&:hover': { borderColor: '#ef4444', color: '#ef4444' }
                        }}
                      >
                        Select Multiple
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* View Profile Button */}
              {careerReports.some(r => r.status === 'completed') && (
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: '#171717',
                    border: '1px solid #00FFF5',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 0.5 }}>
                      Your Career Profile
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#737373' }}>
                      View and share your public profile with progress tracking
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<ProfileIcon />}
                    onClick={() => navigate(`/profiles/${getProfileIdForUser(user.uid)}`)}
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#00FFF5',
                      color: '#0a0a0a',
                      '&:hover': {
                        backgroundColor: '#00CCC5'
                      }
                    }}
                  >
                    View Profile
                  </Button>
                </Paper>
              )}
              
              {careerReports.map((report) => (
                <CareerReportCard
                  key={report.id}
                  report={report}
                  onRefresh={() => loadCareerReports(false)}
                  platformSettings={platformSettings}
                  showCheckbox={bulkDeleteMode}
                  selected={selectedReports.has(report.id)}
                  onSelect={handleReportSelect}
                />
              ))}
            </Box>
          )
        ) : (
          // Cognitive Profiles Tab (now second)
          forecasts.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: '#171717',
                border: '1px solid #262626',
                borderRadius: '12px'
              }}
            >
              <Typography variant="h6" sx={{ color: '#fafafa', mb: 2, fontFamily: 'monospace' }}>
                No cognitive profiles yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373', mb: 3 }}>
                Start your first {platformSettings.platformName || 'Mindtrace'} analysis to decode your cognitive patterns.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#9D00FF',
                  color: '#fafafa',
                  '&:hover': {
                    backgroundColor: '#8000CC'
                  }
                }}
              >
                Start Cognitive Profile
              </Button>
            </Paper>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {forecasts.map((forecast) => (
              <Card
                key={forecast.id}
                sx={{
                  backgroundColor: '#171717',
                  border: '1px solid #262626',
                  borderRadius: '12px',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: '#404040'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ color: '#fafafa', fontFamily: 'monospace', mb: 1 }}
                      >
                        Forecast from {new Date(forecast.createdAt?.toDate?.() || forecast.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#737373', mb: 2 }}>
                        {forecast.structuredData?.chatWordCount || 0} words processed
                      </Typography>
                      
                      {/* Status */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Active Badge */}
                        {forecast.isActive && (
                          <Chip
                            icon={<ActiveIcon sx={{ fontSize: 14, color: '#22c55e !important' }} />}
                            label="ACTIVE"
                            size="small"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              backgroundColor: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e',
                              border: '1px solid #22c55e'
                            }}
                          />
                        )}
                        
                        <Chip
                          label={getStatusText(forecast)}
                          color={getStatusColor(forecast.status)}
                          size="small"
                          sx={{ fontFamily: 'monospace' }}
                        />
                        
                        {/* Archetype Badge */}
                        {forecast.matchedArchetype && (
                          <Chip
                            label={forecast.matchedArchetype.name}
                            size="small"
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: '#9D00FF',
                              color: '#fafafa'
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {/* Retry button for stuck/failed forecasts */}
                      {(forecast.status === 'payment_complete' || forecast.status === 'failed') && (
                        <Button
                          variant="outlined"
                          startIcon={retryingForecast === forecast.id ? <CircularProgress size={16} sx={{ color: '#00FFF5' }} /> : <RetryIcon />}
                          onClick={() => handleRetryAnalysis(forecast)}
                          disabled={retryingForecast === forecast.id}
                          sx={{
                            color: '#00FFF5',
                            borderColor: '#00FFF5',
                            fontFamily: 'monospace',
                            '&:hover': {
                              borderColor: '#00FFF5',
                              backgroundColor: 'rgba(0, 255, 245, 0.1)'
                            }
                          }}
                        >
                          {retryingForecast === forecast.id ? 'Starting...' : 'Retry Analysis'}
                        </Button>
                      )}
                      
                      {/* View/Download/Regenerate buttons for completed forecasts */}
                      {(forecast.status === 'analyzed' || forecast.status === 'completed') && (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewReport(forecast)}
                            disabled={!forecast.pdfUrl && !forecast.aiAnalysis}
                            sx={{
                              color: '#00FFF5',
                              borderColor: '#00FFF5',
                              fontFamily: 'monospace',
                              '&:hover': {
                                borderColor: '#00FFF5',
                                backgroundColor: 'rgba(0, 255, 245, 0.1)'
                              }
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadReport(forecast)}
                            disabled={!forecast.pdfUrl}
                            sx={{
                              backgroundColor: '#fafafa',
                              color: '#0a0a0a',
                              fontFamily: 'monospace',
                              '&:hover': {
                                backgroundColor: '#e5e5e5'
                              }
                            }}
                          >
                            Download
                          </Button>
                          
                          {/* One-time free regenerate */}
                          {!forecast.regenerated && forecast.pdfUrl && (
                            <Button
                              variant="outlined"
                              startIcon={regeneratingForecast === forecast.id ? <CircularProgress size={16} sx={{ color: '#9D00FF' }} /> : <RegenerateIcon />}
                              onClick={() => handleRegenerateReport(forecast)}
                              disabled={regeneratingForecast === forecast.id}
                              sx={{
                                color: '#9D00FF',
                                borderColor: '#9D00FF',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                '&:hover': {
                                  borderColor: '#9D00FF',
                                  backgroundColor: 'rgba(157, 0, 255, 0.1)'
                                }
                              }}
                            >
                              {regeneratingForecast === forecast.id ? 'Regenerating...' : 'Regenerate (Free)'}
                            </Button>
                          )}
                          
                          {/* Set Active Button - only show if completed and not already active */}
                          {!forecast.isActive && (
                            <Button
                              variant="outlined"
                              startIcon={settingActiveForecast === forecast.id ? null : <InactiveIcon />}
                              onClick={() => handleSetActiveForecast(forecast)}
                              disabled={settingActiveForecast === forecast.id}
                              size="small"
                              sx={{
                                color: '#fbbf24',
                                borderColor: '#fbbf24',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                '&:hover': {
                                  borderColor: '#fbbf24',
                                  backgroundColor: 'rgba(251, 191, 36, 0.1)'
                                }
                              }}
                            >
                              {settingActiveForecast === forecast.id ? 'Setting...' : 'Set Active'}
                            </Button>
                          )}
                        </>
                      )}
                      
                      {/* Delete button - always available */}
                      <IconButton
                        onClick={() => handleDeleteReport(forecast)}
                        disabled={deletingForecast === forecast.id}
                        sx={{
                          color: '#ef4444',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)'
                          }
                        }}
                        title="Delete this report"
                      >
                        {deletingForecast === forecast.id ? <CircularProgress size={20} sx={{ color: '#ef4444' }} /> : <DeleteIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Processing indicator */}
                  {(forecast.status === 'processing' || forecast.status === 'payment_complete' || (forecast.status === 'analyzed' && !forecast.pdfUrl)) && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        sx={{
                          backgroundColor: '#262626',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#00FFF5'
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#737373', mt: 1, display: 'block' }}>
                        {forecast.status === 'processing' || forecast.status === 'payment_complete' 
                          ? 'AI is analyzing your patterns. This takes 30-60 seconds...'
                          : 'Generating your professional PDF report. Almost done...'}
                      </Typography>
                    </Box>
                  )}

                  {/* Error message */}
                  {forecast.status === 'failed' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Analysis failed: {forecast.error || 'Unknown error'}
                    </Alert>
                  )}
                </CardContent>
              </Card>
              ))}
            </Box>
          )
        )}
      </Container>
    </Box>
  );
}

export default UserDashboard;

